import { useCallback, useEffect, useRef } from 'react'

type WakeLockSentinelLike = {
  released: boolean
  release: () => Promise<void>
  addEventListener?: (type: 'release', listener: () => void) => void
  removeEventListener?: (type: 'release', listener: () => void) => void
}

type WakeLockNavigator = Navigator & {
  wakeLock?: {
    request: (type: 'screen') => Promise<WakeLockSentinelLike>
  }
}

const useScreenWakeLock = (enabled = true) => {
  const sentinelRef = useRef<WakeLockSentinelLike | null>(null)
  const requestInFlightRef = useRef(false)

  const handleSentinelReleased = useCallback(() => {
    sentinelRef.current = null
  }, [])

  const detachSentinelListener = useCallback(
    (sentinel: WakeLockSentinelLike | null) => {
      sentinel?.removeEventListener?.('release', handleSentinelReleased)
    },
    [handleSentinelReleased],
  )

  const attachSentinelListener = useCallback(
    (sentinel: WakeLockSentinelLike) => {
      sentinel.addEventListener?.('release', handleSentinelReleased)
    },
    [handleSentinelReleased],
  )

  const releaseWakeLock = useCallback(async () => {
    const currentSentinel = sentinelRef.current
    if (!currentSentinel) {
      return
    }

    detachSentinelListener(currentSentinel)
    sentinelRef.current = null

    if (!currentSentinel.released) {
      try {
        await currentSentinel.release()
      } catch {
        // Ignore release errors; sentinel is already detached from component state.
      }
    }
  }, [detachSentinelListener])

  const requestWakeLock = useCallback(async () => {
    if (!enabled) return
    if (typeof window === 'undefined' || typeof document === 'undefined') return
    if (document.visibilityState !== 'visible') return
    if (requestInFlightRef.current) return

    const wakeLockApi = (navigator as WakeLockNavigator).wakeLock
    if (!wakeLockApi?.request) return

    const currentSentinel = sentinelRef.current
    if (currentSentinel && !currentSentinel.released) {
      return
    }

    requestInFlightRef.current = true

    try {
      const nextSentinel = await wakeLockApi.request('screen')
      detachSentinelListener(currentSentinel)
      sentinelRef.current = nextSentinel
      attachSentinelListener(nextSentinel)
    } catch {
      // Wake lock can be denied by browser/user/system policy.
    } finally {
      requestInFlightRef.current = false
    }
  }, [attachSentinelListener, detachSentinelListener, enabled])

  useEffect(() => {
    if (!enabled) {
      void releaseWakeLock()
      return
    }

    void requestWakeLock()

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void requestWakeLock()
        return
      }
      void releaseWakeLock()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', requestWakeLock)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', requestWakeLock)
      void releaseWakeLock()
    }
  }, [enabled, releaseWakeLock, requestWakeLock])
}

export default useScreenWakeLock
